// ─── Open Food Facts — recherche d'un produit par code-barres ─────────────────
// Base ouverte et gratuite (sans clé) : https://world.openfoodfacts.org
// On lit l'API v2 et on mappe les champs utiles vers notre modèle d'ingrédient.
//
//   const p = await chercherProduit('3017620422003');
//   → { trouve: true, nom, marque, unite, nutrition: { kcal, lipides, … } }
//   → { trouve: false } si code inconnu · lève une erreur réseau sinon.
//
// Aucune dépendance native : simple fetch. Fonctionne aussi sur le web.

const BASE = 'https://world.openfoodfacts.org/api/v2/product';
// Champs demandés (limite la charge réseau et respecte l'API).
const CHAMPS = [
  'product_name', 'product_name_fr', 'brands', 'quantity',
  'nutriscore_grade', 'nutriments',
  'image_front_url', 'image_url', 'image_front_small_url',
].join(',');

// Un code-barres valide = 8 à 14 chiffres (EAN-8, UPC-A, EAN-13, ITF-14).
export function codeBarresValide(code) {
  return /^\d{8,14}$/.test(String(code || '').trim());
}

function nombre(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isNaN(n) ? null : Math.round(n * 100) / 100;
}

// « 450 g », « 1,5 L », « 6 x 100 g », « 33 cl »… → { valeur, unite } normalisé.
// On ramène cl→ml et on garde g / kg / ml / l / u.
function parseContenance(txt) {
  const s = String(txt || '').trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  const m = s.match(/([\d.]+)\s*(kg|g|cl|ml|l)\b/);
  if (!m) return null;
  let val = Number(m[1]);
  let u = m[2];
  if (isNaN(val) || val <= 0) return null;
  // Multipack « 6 x 100 g » → total.
  const mult = s.match(/(\d+)\s*[x×]\s*[\d.]/);
  if (mult) val = val * Number(mult[1]);
  if (u === 'cl') { val = val * 10; u = 'ml'; }
  return { valeur: Math.round(val * 100) / 100, unite: u };
}

// `nutriments` OFF donne les valeurs pour 100 g/mL sous des clés « *_100g ».
function mapNutrition(n) {
  if (!n) return null;
  const out = {
    // energy-kcal_100g est le plus fiable ; sinon on convertit les kJ.
    kcal:      nombre(n['energy-kcal_100g']) ?? (n['energy_100g'] != null ? Math.round(n['energy_100g'] / 4.184) : null),
    lipides:   nombre(n['fat_100g']),
    satures:   nombre(n['saturated-fat_100g']),
    glucides:  nombre(n['carbohydrates_100g']),
    sucres:    nombre(n['sugars_100g']),
    proteines: nombre(n['proteins_100g']),
    sel:       nombre(n['salt_100g']),
    fibres:    nombre(n['fiber_100g']),
  };
  // Retire les null pour ne pas écrire de champs vides.
  const clean = {};
  for (const k of Object.keys(out)) if (out[k] !== null) clean[k] = out[k];
  return Object.keys(clean).length ? clean : null;
}

export async function chercherProduit(code) {
  const c = String(code || '').trim();
  if (!codeBarresValide(c)) return { trouve: false, raison: 'code' };

  const url = `${BASE}/${encodeURIComponent(c)}.json?fields=${CHAMPS}`;
  const res = await fetch(url, { headers: { 'User-Agent': 'MoPCal/1.0 (app familiale)' } });
  // 404 = produit absent de la base (l'API v2 répond 404, pas 200/status:0).
  // On le distingue d'une vraie panne réseau (5xx / fetch qui échoue).
  if (res.status === 404) return { trouve: false, raison: 'inconnu' };
  if (!res.ok) throw new Error('reseau');
  const data = await res.json();

  // status 0 = produit inconnu (certaines routes répondent 200 quand même).
  if (data.status === 0 || !data.product) return { trouve: false, raison: 'inconnu' };

  const p = data.product;
  const nom = (p.product_name_fr || p.product_name || '').trim();
  const marque = (p.brands || '').split(',')[0].trim();
  const cont = parseContenance(p.quantity);
  const nutrition = mapNutrition(p.nutriments);
  const nutriScore = (p.nutriscore_grade || '').trim().toLowerCase();
  if (nutrition && ['a', 'b', 'c', 'd', 'e'].includes(nutriScore)) nutrition.nutriScore = nutriScore;

  const photoUrl = (p.image_front_url || p.image_url || p.image_front_small_url || '').trim() || null;

  return {
    trouve: true,
    code: c,
    nom,
    marque,
    photoUrl,
    // Contenance du paquet (ex. 450 g) : sert à ramener le prix à l'unité.
    contenance:      cont ? cont.valeur : null,
    contenanceUnite: cont ? cont.unite : '',
    // L'unité de vente d'un produit emballé est peu utile pour une recette :
    // on laisse l'utilisateur choisir (défaut « u »), la nutrition étant /100 g.
    unite: '',
    nutrition,
  };
}
